import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { menuItems } from '../../mocks/menuData';

export default function OrderPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [cart, setCart] = useState<Array<{ id: number; name: string; price: string; quantity: number }>>([]);

  const categories = ['Todos', 'Bocadillos', 'Durum', 'Pizzas Lawasa', 'Platos', 'Raciones', 'Menús', 'Menús Fried Chicken', 'Bebidas'];

  // Filtrar productos por búsqueda y categoría
  const filteredItems = menuItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'Todos' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const addToCart = (item: typeof menuItems[0]) => {
    const existingItem = cart.find(cartItem => cartItem.id === item.id);
    if (existingItem) {
      setCart(cart.map(cartItem =>
        cartItem.id === item.id
          ? { ...cartItem, quantity: cartItem.quantity + 1 }
          : cartItem
      ));
    } else {
      setCart([...cart, { id: item.id, name: item.name, price: item.price, quantity: 1 }]);
    }
  };

  const removeFromCart = (id: number) => {
    const existingItem = cart.find(cartItem => cartItem.id === id);
    if (existingItem && existingItem.quantity > 1) {
      setCart(cart.map(cartItem =>
        cartItem.id === id
          ? { ...cartItem, quantity: cartItem.quantity - 1 }
          : cartItem
      ));
    } else {
      setCart(cart.filter(cartItem => cartItem.id !== id));
    }
  };

  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => {
      const price = parseFloat(item.price.replace('€', '').replace(',', '.'));
      return total + (price * item.quantity);
    }, 0).toFixed(2);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-12 px-6 lg:px-20 bg-gradient-to-br from-[#E63946] to-[#D62839]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-4">
              Haz tu pedido
            </h1>
            <p className="text-xl text-white/90">
              Busca tus productos favoritos y añádelos a tu pedido
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-3xl mx-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar kebab, durum, menú..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-6 py-5 pl-14 rounded-2xl text-lg focus:outline-none focus:ring-4 focus:ring-white/30 shadow-xl"
              />
              <i className="ri-search-line absolute left-5 top-1/2 -translate-y-1/2 text-2xl text-gray-400"></i>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-6 px-6 lg:px-20 bg-white sticky top-20 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-5 py-2 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer text-sm ${
                  selectedCategory === category
                    ? 'bg-[#E63946] text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {searchQuery ? `Resultados para "${searchQuery}"` : selectedCategory}
              </h2>
              <p className="text-gray-600">
                {filteredItems.length} {filteredItems.length === 1 ? 'producto encontrado' : 'productos encontrados'}
              </p>
            </div>
            
            {/* Cart Summary */}
            {cart.length > 0 && (
              <div className="bg-[#E63946] text-white px-6 py-3 rounded-full font-semibold shadow-lg">
                <i className="ri-shopping-cart-fill mr-2"></i>
                {getTotalItems()} {getTotalItems() === 1 ? 'producto' : 'productos'} - {getTotalPrice()}€
              </div>
            )}
          </div>

          {/* Products */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item) => {
              const cartItem = cart.find(c => c.id === item.id);
              const quantity = cartItem?.quantity || 0;

              return (
                <div
                  key={item.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group"
                >
                  {/* Image */}
                  <div className="relative h-48 w-full overflow-hidden bg-gray-100">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3 bg-[#E63946] text-white px-3 py-1 rounded-full text-xs font-semibold">
                      {item.category}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {item.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {item.description}
                    </p>
                    
                    {/* Price and Add Button */}
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-[#E63946]">
                        {item.price}
                      </span>
                      
                      {quantity === 0 ? (
                        <button
                          onClick={() => addToCart(item)}
                          className="bg-[#E63946] text-white px-5 py-2 rounded-full font-semibold hover:bg-[#D62839] transition-all hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
                        >
                          <i className="ri-add-line"></i>
                          Añadir
                        </button>
                      ) : (
                        <div className="flex items-center gap-3 bg-gray-100 rounded-full px-2 py-1">
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="w-8 h-8 flex items-center justify-center bg-white rounded-full hover:bg-gray-200 transition-colors cursor-pointer"
                          >
                            <i className="ri-subtract-line text-[#E63946]"></i>
                          </button>
                          <span className="font-bold text-gray-900 min-w-[20px] text-center">
                            {quantity}
                          </span>
                          <button
                            onClick={() => addToCart(item)}
                            className="w-8 h-8 flex items-center justify-center bg-white rounded-full hover:bg-gray-200 transition-colors cursor-pointer"
                          >
                            <i className="ri-add-line text-[#E63946]"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Empty State */}
          {filteredItems.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-xl text-gray-500 mb-2">No se encontraron productos</p>
              <p className="text-gray-400">Intenta con otra búsqueda o categoría</p>
            </div>
          )}
        </div>
      </section>

      {/* Floating Cart Button */}
      {cart.length > 0 && (
        <div className="fixed bottom-8 right-8 z-50">
          <a
            href={`https://wa.me/34919123048?text=${encodeURIComponent(
              `¡Hola! Quiero hacer un pedido:\n\n${cart.map(item => `${item.quantity}x ${item.name} - ${item.price}`).join('\n')}\n\nTotal: ${getTotalPrice()}€`
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-[#25D366] text-white px-8 py-5 rounded-full font-bold text-lg shadow-2xl hover:bg-[#20BA5A] transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-fill text-2xl"></i>
            Enviar pedido ({getTotalItems()})
          </a>
        </div>
      )}

      <Footer />
    </div>
  );
}
